import { Controller } from "@hotwired/stimulus"

// Pflege-Rente-Rechner: Rentenbeiträge der Pflegekasse und Rentenplus für
// pflegende Angehörige. Alle Rechengrößen kommen server-seitig als JSON
// (PflegeRenteRechnerController#show), gerechnet wird nur hier.
export default class extends Controller {
  static values = { data: Object }

  static targets = [
    "pflegegradButton",
    "pg1Note",
    "leistungsartSelect",
    "pflegeStundenInput",
    "erwerbStundenInput",
    "dauerInput",
    "vollrenteCheckbox",

    "resultPlus",
    "entgelt",
    "beitrag",
    "epJahr",
    "hochrechnungDauer",
    "hochrechnungPlus",
    "hochrechnungEp",

    "warnErwerb",
    "warnMindest",
    "warnMindestStunden",
    "teilrenteHint",

    "stepProzentsatz",
    "stepEntgelt",
    "stepBeitrag",
    "stepEp",
    "stepPlus",
    "stepGesamtDauer",
    "stepGesamt"
  ]

  connect() {
    this.euroFormatter = new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR", minimumFractionDigits: 2, maximumFractionDigits: 2 })
    this.epFormatter = new Intl.NumberFormat("de-DE", { minimumFractionDigits: 3, maximumFractionDigits: 3 })
    this.prozentFormatter = new Intl.NumberFormat("de-DE", { style: "percent", minimumFractionDigits: 2, maximumFractionDigits: 2 })
    this.jahreFormatter = new Intl.NumberFormat("de-DE", { maximumFractionDigits: 1 })
    this.selectPflegegradValue(3)
  }

  selectPflegegrad(event) {
    this.selectPflegegradValue(parseInt(event.currentTarget.dataset.value))
  }

  selectPflegegradValue(pg) {
    this.selectedPflegegrad = pg

    this.pflegegradButtonTargets.forEach(btn => {
      const isSelected = parseInt(btn.dataset.value) === pg
      btn.classList.toggle("border-ph24_blue", isSelected)
      btn.classList.toggle("bg-ph24_blue/5", isSelected)
      btn.classList.toggle("ring-2", isSelected)
      btn.classList.toggle("ring-ph24_blue", isSelected)
      btn.classList.toggle("border-ph24_blue/20", !isSelected)
      btn.classList.toggle("hover:border-ph24_blue/40", !isSelected)
    })

    if (this.hasPg1NoteTarget) this.pg1NoteTarget.classList.toggle("hidden", pg !== 1)

    this.calculate()
  }

  calculate() {
    if (!this.selectedPflegegrad) return
    const data = this.dataValue
    const pg = this.selectedPflegegrad

    const pflegeStunden = this.clamp(parseFloat(this.pflegeStundenInputTarget.value), 0, 100, 0)
    const erwerbStunden = this.clamp(parseFloat(this.erwerbStundenInputTarget.value), 0, 100, 0)
    const dauer = this.clamp(parseFloat(this.dauerInputTarget.value), 0.5, 20, 1)

    // Ausschlussgründe: PG 1 und mehr als 30 Wochenstunden Erwerbstätigkeit
    // (genau 30,00 h sind noch zulässig). Unter 10 Pflegestunden nur Hinweis,
    // das Ergebnis wird trotzdem angezeigt.
    const keinePg = pg === 1
    const erwerbZuViel = erwerbStunden > data.max_erwerb_stunden
    const unterMindest = !keinePg && !erwerbZuViel && pflegeStunden < data.min_pflegestunden

    const prozentsatz = keinePg || erwerbZuViel ? 0 : data.prozentsaetze[pg][this.leistungsartSelectTarget.value]
    const entgelt = data.bezugsgroesse * prozentsatz
    const beitrag = entgelt * data.beitragssatz
    const epJahr = (entgelt * 12) / data.durchschnittsentgelt
    const plusJahr = epJahr * data.rentenwert
    const epGesamt = epJahr * dauer
    const plusGesamt = plusJahr * dauer

    this.warnErwerbTarget.classList.toggle("hidden", !erwerbZuViel)
    this.warnMindestTarget.classList.toggle("hidden", !unterMindest)
    if (unterMindest) this.warnMindestStundenTarget.textContent = this.jahreFormatter.format(pflegeStunden)
    this.teilrenteHintTarget.classList.toggle("hidden", !this.vollrenteCheckboxTarget.checked)

    this.resultPlusTarget.textContent = this.euroFormatter.format(plusJahr)
    this.entgeltTarget.textContent = this.euroFormatter.format(entgelt)
    this.beitragTarget.textContent = this.euroFormatter.format(beitrag)
    this.epJahrTarget.textContent = this.epFormatter.format(epJahr)

    this.hochrechnungDauerTarget.textContent = this.formatJahre(dauer)
    this.hochrechnungPlusTarget.textContent = this.euroFormatter.format(plusGesamt)
    this.hochrechnungEpTarget.textContent = this.epFormatter.format(epGesamt)

    this.stepProzentsatzTarget.textContent = this.prozentFormatter.format(prozentsatz)
    this.stepEntgeltTarget.textContent = this.euroFormatter.format(entgelt)
    this.stepBeitragTarget.textContent = this.euroFormatter.format(beitrag)
    this.stepEpTarget.textContent = this.epFormatter.format(epJahr)
    this.stepPlusTarget.textContent = this.euroFormatter.format(plusJahr)
    this.stepGesamtDauerTarget.textContent = this.formatJahre(dauer)
    this.stepGesamtTarget.textContent = this.euroFormatter.format(plusGesamt)
  }

  clamp(value, min, max, fallback) {
    if (isNaN(value)) return fallback
    return Math.min(max, Math.max(min, value))
  }

  formatJahre(dauer) {
    return `${this.jahreFormatter.format(dauer)} ${dauer === 1 ? "Jahr" : "Jahren"}`
  }
}
